import { ApplicationInsights, ITelemetryItem, SeverityLevel } from '@microsoft/applicationinsights-web';
import type { UserState } from './store/User';
import type { TenantState } from './store/Tenant';
import { Dictionary } from './entities/common';

const CLOUD_ROLE_NAME = 'ppmx-web-client';
const STORE_AUTHENTICATED_USER_IN_COOKIE = false;
const ENTITY_ID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24}/gi;
const REGION_AND_TENANT_PREFIX_PATTERN = /^\/[^/]+\/@[^/]+/;

let appInsights: ApplicationInsights | undefined;
let tenantProperties: Dictionary<string> = {};

const stripRegionAndTenantPrefix = (path: string): string => path.replace(REGION_AND_TENANT_PREFIX_PATTERN, '');

const replaceEntityIds = (path: string): string => path.replace(ENTITY_ID_PATTERN, '{id}');

const normalizePath = (path: string): string => replaceEntityIds(stripRegionAndTenantPrefix(path));

export const normalizeTelemetryName = (name?: string): string | undefined => {
    if (!name) {
        return name;
    }

    const httpMethodSeparatorIndex = name.indexOf(' ');
    if (httpMethodSeparatorIndex < 0) {
        return normalizePath(name);
    }

    const httpMethod = name.substring(0, httpMethodSeparatorIndex);
    const path = name.substring(httpMethodSeparatorIndex + 1);
    return `${httpMethod} ${normalizePath(path)}`;
}

const tryParseUrl = (url?: string): URL | undefined => {
    try {
        return url ? new URL(url, window.location.href) : undefined;
    } catch {
        return undefined;
    }
}

const isDependencyOnAnotherHost = (item: ITelemetryItem): boolean => {
    const targetHost = tryParseUrl(item.baseData?.target)?.host;
    return item.baseType === 'RemoteDependencyData' && !!targetHost && targetHost !== window.location.host;
}

const normalizeDependencyName = (name?: string): string | undefined => {
    const httpMethodSeparatorIndex = name?.indexOf(' ') ?? -1;
    if (!name || httpMethodSeparatorIndex < 0) {
        return normalizeTelemetryName(name);
    }

    const httpMethod = name.substring(0, httpMethodSeparatorIndex);
    const requestUrl = name.substring(httpMethodSeparatorIndex + 1);
    const pathWithoutQuery = tryParseUrl(requestUrl)?.pathname ?? requestUrl;
    return normalizeTelemetryName(`${httpMethod} ${pathWithoutQuery}`);
}

export const enrichTelemetryItem = (item: ITelemetryItem): boolean => {
    if (isDependencyOnAnotherHost(item)) {
        return false;
    }

    if (item.baseType === 'RemoteDependencyData') {
        item.baseData!.name = normalizeDependencyName(item.baseData!.name);
        const dependencyUrl = tryParseUrl(item.baseData!.data);
        if (dependencyUrl) {
            dependencyUrl.search = '';
            dependencyUrl.hash = '';
            item.baseData!.data = dependencyUrl.href;
        }
    } else if (item.baseType === 'PageviewData') {
        item.baseData!.name = normalizeTelemetryName(item.baseData!.name);
    }

    item.tags = item.tags ?? [];
    item.tags['ai.cloud.role'] = CLOUD_ROLE_NAME;
    item.data = { ...item.data, ...tenantProperties };
    return true;
}

export const setTenantProperties = (properties: Dictionary<string>) => {
    tenantProperties = properties;
}

const createApplicationInsights = (connectionString: string): ApplicationInsights =>
    new ApplicationInsights({
        config: {
            connectionString,
            disableCookiesUsage: true,
            enableAutoRouteTracking: false,
            autoTrackPageVisitTime: false,
            enableAjaxPerfTracking: true,
            // the default of 500 per page view silently stops tracking in a long-lived SPA session
            maxAjaxCallsPerView: -1,
            enableCorsCorrelation: false
        }
    });

export const load = (connectionString?: string | null) => {
    if (!connectionString || appInsights) {
        return;
    }

    try {
        appInsights = createApplicationInsights(connectionString);
        appInsights.loadAppInsights();
        appInsights.addTelemetryInitializer(enrichTelemetryItem);
    } catch (error) {
        appInsights = undefined;
        console.error(error);
    }
}

export const identify = (user: Pick<UserState, 'id'>, tenant: Pick<TenantState, 'id' | 'region' | 'subscription'>) => {
    if (!appInsights || !user.id || !tenant.id) {
        return;
    }

    appInsights.setAuthenticatedUserContext(user.id, tenant.id, STORE_AUTHENTICATED_USER_IN_COOKIE);
    setTenantProperties({
        tenantId: tenant.id,
        region: tenant.region,
        plan: String(tenant.subscription?.plan ?? '')
    });
}

export const trackPage = (pathname: string) => {
    const normalizedPath = normalizePath(pathname);
    appInsights?.trackPageView({ name: normalizedPath, uri: new URL(normalizedPath, window.location.origin).href });
}

const serialize = (value: unknown): string => {
    try {
        return JSON.stringify(value) ?? 'undefined';
    } catch {
        return 'unserializable value';
    }
}

const toError = (value: unknown): Error => {
    if (value instanceof Error) {
        return value;
    }

    return new Error(typeof value === 'string' ? value : serialize(value));
}

export const trackException = (error: unknown, properties?: Dictionary<any>) => {
    appInsights?.trackException({ exception: toError(error), severityLevel: SeverityLevel.Error }, properties);
}

export const trackEvent = (name: string, properties?: Dictionary<any>, measurements?: Dictionary<number>) => {
    appInsights?.trackEvent({ name, properties, measurements });
}
